import store from '@/store';
import request from '@/utils/request';

const GATEWAY_PARAM_KEY = 'gateway_id';

/**
 * 判定用ヘルパー。gateway_id が含まれていればサービス ID を付与すべきとみなす。
 */
function hasGatewayKey(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(payload, GATEWAY_PARAM_KEY);
}

/**
 * params / data に service_id が無ければ指定した ID を差し込む。
 */
function injectServiceFields(payload, service) {
  const target = payload ? { ...payload } : {};
  if (!service) {
    return target;
  }
  if (target.service_id === undefined) {
    target.service_id = service;
  }
  return target;
}

/**
 * normalizeRequirement が受け取る evaluator の戻り値を共通形式に整える。
 * boolean / string / object のいずれでも `{ require, serviceId }` に変換する。
 */
function interpretEvaluatorResult(decision, context) {
  if (typeof decision === 'boolean') {
    return { require: decision };
  }

  if (typeof decision === 'string') {
    return {
      require: true,
      serviceId: decision,
    };
  }

  if (decision && typeof decision === 'object') {
    const requireFlag = decision.require ?? decision.required ?? true;
    let serviceId;

    if (decision.serviceId !== undefined) {
      serviceId = context.resolveService(decision.serviceId);
    }

    return {
      require: requireFlag,
      serviceId,
    };
  }

  return { require: false };
}

/**
 * requireService に固定値を渡した場合の判定情報を作る。
 * `spec` の書き方を吸収して `{ require, serviceId }` にまとめる。
 */
function describeFixedRequirement(spec, defaultServiceId, resolveService) {
  if (spec === undefined || spec === null) {
    return { require: true, serviceId: defaultServiceId };
  }

  if (typeof spec === 'string') {
    return { require: true, serviceId: spec };
  }

  if (typeof spec === 'object') {
    const requireFlag = spec.require ?? spec.required ?? true;
    const serviceId = spec.serviceId;
    return {
      require: requireFlag,
      serviceId: serviceId ?? defaultServiceId,
    };
  }

  return { require: true, serviceId: defaultServiceId };
}

/**
 * オプションで指定された serviceId を優先しつつ ID を解決する。
 */
function resolveWithOptions(serviceIdValue, options) {
  const normalizedServiceId = serviceIdValue ?? options.serviceId;
  return resolveServiceId(normalizedServiceId);
}

/**
 * params / data ごとに「サービス ID を付与するか」を判定し、必要なら ID を指定する。
 * evaluator が無い場合は gateway_id の有無から自動判定する。
 */
function normalizeRequirement(payload, evaluator, context) {
  if (!evaluator) {
    return { require: hasGatewayKey(payload) };
  }

  const decision = evaluator(payload, context.defaultServiceId, context.resolveService);
  return interpretEvaluatorResult(decision, context);
}

/**
 * serviceIdValue が指定されていればそれを返し、なければ store の basicServiceId を使用する。
 * 見つからない場合は undefined を返しつつ警告を出す。
 */
export function resolveServiceId(serviceIdValue) {
  if (serviceIdValue) {
    return serviceIdValue;
  }

  const basicFromStore = store.getters.basicServiceId;
  if (basicFromStore) {
    return basicFromStore;
  }

  console.warn('[serviceContext] service_id could not be resolved');
  return undefined;
}

export function getBasicServiceId() {
  return store.getters.basicServiceId ?? undefined;
}

/**
 * 与えられた payload に既定のサービス ID を付与するユーティリティ。
 */
export function attachServiceId(payload = {}, options = {}) {
  const service = resolveServiceId(options.serviceId);
  return injectServiceFields(payload, service);
}

/**
 * gatewayPermissionRequest の params/data に対してサービス ID の付与条件を記述するためのヘルパー。
 * 呼び出し元は function / string / object / undefined のいずれかで指定できる。
 * 返り値は normalizeRequirement から評価され、最終的な付与有無が決定される。
 */
export function requireService(spec) {
  return (_, defaultServiceId, resolveService) => {
    if (typeof spec === 'function') {
      return spec(_, defaultServiceId, resolveService);
    }

    return describeFixedRequirement(spec, defaultServiceId, resolveService);
  };
}

/**
 * サービス権限付きリクエストを共通処理で実行する。
 * 1. options から既定サービス ID を解決
 * 2. params/data ごとに requireService などの結果を評価
 * 3. 必要に応じて service_id を注入
 * 4. 最終的なリクエストを送信
 * 初学者向け補足: params はクエリ、data はボディ。service_id を足すかどうかを毎リクエストで判断している。
 */
export function gatewayPermissionRequest(config, options = {}) {
  const nextConfig = { ...config };
  const defaultServiceId = resolveServiceId(options.serviceId);
  const context = {
    defaultServiceId,
    resolveService: (serviceIdValue) => resolveWithOptions(serviceIdValue, options),
  };

  const paramsDecision = normalizeRequirement(nextConfig.params, options.paramsRequireService, context);
  const dataDecision = normalizeRequirement(nextConfig.data, options.dataRequireService, context);

  if (paramsDecision.require && nextConfig.params) {
    const serviceId = paramsDecision.serviceId ?? context.defaultServiceId;
    nextConfig.params = injectServiceFields(nextConfig.params, serviceId);
  }
  if (dataDecision.require && nextConfig.data) {
    const serviceId = dataDecision.serviceId ?? context.defaultServiceId;
    nextConfig.data = injectServiceFields(nextConfig.data, serviceId);
  }
  return request(nextConfig);
}
